import {
  IconAnalytics,
  IconBinocularsGray,
  IconChartBar,
  IconClockGray,
  IconCoil,
  IconLayers,
  IconLocation,
  IconMeter,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconShareGray,
  IconWarning,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import video from '@src/assets/images/solution/logisticsAsset/video/rtlsVideo.mp4';

import subVideo1 from '@src/assets/images/solution/logisticsAsset/video/sub/rtlsVideo1.mp4';
import subVideo2 from '@src/assets/images/solution/logisticsAsset/video/sub/rtlsVideo2.mp4';
import subVideo3 from '@src/assets/images/solution/logisticsAsset/video/sub/rtlsVideo3.mp4';
import subVideo4 from '@src/assets/images/solution/logisticsAsset/video/sub/rtlsVideo4.mp4';

import image1 from '@src/assets/images/solution/logisticsAsset/img/1.avif';
import image2 from '@src/assets/images/solution/logisticsAsset/img/2.avif';
import image3 from '@src/assets/images/solution/logisticsAsset/img/3.avif';
import image4 from '@src/assets/images/solution/logisticsAsset/img/4.avif';

export default {
  type: 'Logistics Solution',
  banner: {
    title: 'Automatically record asset movements and control in/out operations in real time',
    desc: 'Automatically track asset check-in/out times and routes to digitize logistics flows, enabling accurate inventory checks and anomaly detection without manual work.',
  },

  video: {
    title: 'Enhance warehouse efficiency with precise location data.',
    desc: 'Track each asset’s location in real time, even in complex warehouses, maximizing the accuracy and efficiency of asset management.',
    play: video,
  },

  func: [
    {
      theme: 'Accurately track and manage logistics asset locations.',
      title: 'Real-time asset location monitoring',
      desc: 'Pinpoint the location of individual assets within a large warehouse with ±50cm accuracy, and check asset status and distribution in real time. Enables fast and accurate inventory control.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Fast data integration',
      icon2: IconShareGray,
      image: image1,
    },
    {
      title: 'Simplified asset management with ultra-small sticker tags',
      desc: 'Attach ultra-small sticker tags to assets for easy management. Apply location tracking to assets of any size or shape.',
      sub1: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High accuracy',
      icon2: IconRulerGray,
      image: image2,
    },
    {
      title: 'System optimized for large-scale asset management',
      desc: 'Monitor and manage over 10,000 inventory assets simultaneously in real time, ensuring high efficiency in large-scale logistics environments.',
      sub1: 'Fast data integration',
      icon1: IconShareGray,
      sub2: 'Real-time status monitoring',
      icon2: IconClockGray,
      image: image3,
    },
    {
      title: 'Search and locate assets instantly',
      desc: 'Use powerful search features to quickly locate needed assets, minimizing time and effort for inventory management.',
      sub1: 'Fast data integration',
      icon1: IconShareGray,
      sub2: 'Real-time status monitoring',
      icon2: IconClockGray,
      video: subVideo3,
    },
    {
      theme: 'Leverage asset data to boost operational efficiency and prevent risks.',
      title: 'Real-time in/out status tracking',
      desc: 'Monitor all logistics movements—both outbound and inbound—in real time to accurately track flows and prevent operational errors in advance.',
      sub1: 'Fast data integration',
      icon1: IconShareGray,
      sub2: 'Real-time status monitoring',
      icon2: IconClockGray,
      video: subVideo4,
    },
    {
      title: 'Monitor optimal stock levels per warehouse',
      desc: 'Analyze proper stock levels per warehouse in real time to optimize space usage and resolve space shortage issues before they occur.',
      sub1: 'Fast data integration',
      icon1: IconShareGray,
      sub2: 'Real-time status monitoring',
      icon2: IconClockGray,
      image: image4,
    },
    {
      title: 'Unauthorized removal & asset loss alerts',
      desc: 'Immediately send alerts when assets are removed without authorization or moved outside allowed timeframes, preventing asset loss risks.',
      sub1: 'Fast data integration',
      icon1: IconShareGray,
      sub2: 'Real-time status monitoring',
      icon2: IconClockGray,
      video: subVideo1,
    },
    {
      title: 'Real-time inventory monitoring',
      desc: 'Check real-time inventory status intuitively by zone and for the whole warehouse, supporting precise inventory operation and quick decision-making.',
      sub1: 'Fast data integration',
      icon1: IconShareGray,
      sub2: 'Real-time status monitoring',
      icon2: IconClockGray,
      video: subVideo2,
    },
  ],

  subfunc: {
    title: 'Key Features Overview',
    desc: '',

    data: [
      {
        icon: IconLocation,
        title: 'Real-time asset location tracking',
        desc: 'Accurately check the real-time location of all warehouse assets.',
      },
      {
        icon: IconCoil,
        title: 'Ultra-small sticker tags',
        desc: 'Easily attach small tags to various assets for simple tracking.',
      },
      {
        icon: IconLayers,
        title: 'Simultaneous large-scale asset management',
        desc: 'Manage over 10,000 assets simultaneously in real time.',
      },
      {
        icon: IconSearch,
        title: 'Asset location search',
        desc: 'Quickly find asset locations with an easy search function.',
      },
      {
        icon: IconRoute,
        title: 'Real-time in/out tracking',
        desc: 'Monitor check-in and check-out status accurately in real time.',
      },
      {
        icon: IconChartBar,
        title: 'Warehouse stock level analysis',
        desc: 'Analyze stock levels and available space per warehouse for efficient operation.',
      },
      {
        icon: IconWarning,
        title: 'Unauthorized removal alerts',
        desc: 'Instantly alert for unauthorized removal or movement outside allowed times.',
      },
      {
        icon: IconMeter,
        title: 'Real-time inventory status',
        desc: 'Intuitively monitor inventory status for the whole warehouse and by zone in real time.',
      },
      {
        icon: IconRoute,
        title: 'Asset movement history',
        desc: 'Automatically record asset movement routes and store as data.',
      },
      {
        icon: IconAnalytics,
        title: 'Asset data reporting',
        desc: 'Automatically analyze asset movement data and provide regular management reports.',
      },
    ],
  },

  system: {
    title: 'System Structure',
    img: rtlsSystem,
  },

  productTitle: 'Product Packages',
  product: [
    {
      tag: 'assetPackage',
    },
    {
      tag: 'workerPackage',
    },
  ],

  contents: [
    {
      data: 'logisticsSafety',
    },
    {
      data: 'constructionAsset',
    },
    {
      data: 'blogRTLS',
    },
    {
      data: 'blogForklift',
    },
  ],
};
